---
title: "What Is MDX?"
slug: "what-is-mdx"
excerpt: "The Acura MDX is a three-row, seven- or eight-passenger luxury crossover SUV known for its blend of performance, comfort, and advanced features. It..."
publishedAt: "2026-05-25"
updatedAt: "2026-05-25"
author: "maya-lestari"
category: "Development"
tags:
  - markdown
  - jsx
  - react
  - components
featuredImage: "https://www.edmunds.com/assets/m/cs/bltcab2c3fcd8d18233/6685a1510325452b9188287d/2025-Acura-MDX-Type-S-f34-06282024-01-edmunds_1600.jpg"
status: "published"
draft: false
---

## Short Answer

<ShortAnswer>
The Acura MDX is a three-row, seven- or eight-passenger luxury crossover SUV known for its blend of performance, comfort, and advanced features. It is built on Honda’s Global Light Truck platform, which also underpins the [Honda Pilot](# "Honda Pilot") and [Acura RDX](# "Acura RDX"). The MDX lineup includes base models, hybrid variants, and high-performance Type S editions, with engine options ranging from naturally aspirated V6s to turbocharged powertrains.
</ShortAnswer>


## Infobox

<InfoBox title="Acura MDX">
<ul>
<li><strong>Manufacturer:</strong> Acura</li>
<li><strong>Also called:</strong> Honda MDX (first generation)</li>
<li><strong>Production:</strong> 2000–present</li>
<li><strong>Model code(s):</strong> YD1/2/3 (first gen), YD5/6/7 (second gen), YD8/9 (third gen)</li>
<li><strong>Class:</strong> Mid-size crossover SUV</li>
<li><strong>Body style:</strong> 5-door SUV</li>
<li><strong>Layout:</strong> Front-engine, front-wheel-drive or all-wheel-drive</li>
<li><strong>Platform:</strong> Honda Global Light Truck platform</li>
<li><strong>Related:</strong> Honda Pilot, Acura RDX</li>
</ul>
</InfoBox>

## Overview

The Acura MDX is a three-row, seven- or eight-passenger luxury crossover SUV known for its blend of performance, comfort, and advanced features. It is built on Honda’s Global Light Truck platform, which also underpins the [Honda Pilot](# "Honda Pilot") and [Acura RDX](# "Acura RDX"). The MDX lineup includes base models, hybrid variants, and high-performance Type S editions, with engine options ranging from naturally aspirated V6s to turbocharged powertrains.

Standard features often include Acura’s [Super Handling All-Wheel Drive (SH-AWD)](# "Super Handling All-Wheel Drive"), a premium audio system, leather upholstery, and advanced driver-assistance systems (ADAS) such as adaptive cruise control and lane-keeping assist. The MDX is marketed primarily in North America, with limited availability in other regions.

### ### Trim Levels and Variants **Base MDX:** Typically powered by a 3.5-liter V6 engine producing around 290–300 horsepower, paired with a 9-speed automatic transmission.
- **MDX Hybrid:** Introduced in 2022, combines a 3.0-liter V6 with an electric motor for improved fuel efficiency.
- **MDX Type S:** A performance-oriented variant featuring a turbocharged 3.0-liter V6 engine, upgraded suspension, and sport-tuned exhaust, delivering over 350 horsepower.

## History / Background

The Acura MDX was launched in 2000 as Acura’s first SUV, filling a gap in the luxury crossover market. It was developed alongside the second-generation [Honda Odyssey](# "Honda Odyssey") minivan, sharing its platform but offering a more upscale interior and features. The first-generation MDX (YD1) was praised for its spacious three-row cabin, smooth ride, and strong V6 performance.

In 2006, Acura introduced the second-generation MDX (YD2), featuring a revised exterior, improved interior materials, and the addition of a hybrid model in 2007. The third generation (YD5, 2013) brought a more aggressive design, a 9-speed transmission, and enhanced technology, including a larger touchscreen infotainment system.

The fourth generation (YD8, 2022) marked a significant shift with the introduction of the MDX Hybrid and the high-performance Type S variant. The Type S, launched in 2021 for the 2022 model year, represented Acura’s first turbocharged MDX and its most powerful SUV to date.

## How It Works

The Acura MDX operates as a front-wheel-drive or all-wheel-drive (AWD) crossover SUV, utilizing a unibody construction for a balance of ride comfort and handling agility. The powertrain varies by model:

- **Internal Combustion Models:** The base V6 engine (3.5L or 3.0L turbo) drives the front wheels or all four wheels via SH-AWD, which can send up to 70% of torque to the rear axle and distribute it between the rear wheels for enhanced cornering performance.
- **Hybrid Model:** The MDX Hybrid pairs a 3.0L V6 with a 47 hp electric motor and a 1.3 kWh battery, offering improved fuel economy and reduced emissions. The system uses regenerative braking to recharge the battery and can operate in electric-only mode at low speeds.
- **Transmission:** All models use a 9-speed automatic transmission with paddle shifters, optimized for smooth acceleration and efficient gearing.
 
The MDX’s suspension system combines MacPherson struts in the front with a multi-link setup in the rear, tuned for a comfortable ride while maintaining stability during spirited driving. Advanced safety features, such as Acura’s [AcuraWatch](# "AcuraWatch") suite, include collision mitigation braking, road departure mitigation, and adaptive cruise control.

## Important Facts

<FactList>
<ul>
<li><strong>First Luxury Three-Row SUV:</strong> The MDX was Acura’s first three-row SUV and one of the earliest luxury crossovers in its class.</li>
<li><strong>Sales Success:</strong> The MDX has consistently been one of Acura’s best-selling models, outselling the brand’s sedans and coupes in many years.</li>
<li><strong>Type S Performance:</strong> The MDX Type S accelerates from 0–60 mph in approximately 5.5 seconds, making it one of the quickest three-row SUVs in its segment.</li>
<li><strong>Hybrid Efficiency:</strong> The MDX Hybrid achieves an EPA-estimated 25 mpg city / 28 mpg highway, a significant improvement over the base V6 model.</li>
<li><strong>Global Platform:</strong> The MDX shares its underpinnings with the Honda Pilot and Acura RDX, allowing for cost efficiencies and shared component development.</li>
<li><strong>Redesign Frequency:</strong> The MDX undergoes a major redesign every 5–6 years, with mid-cycle updates introducing new features and styling changes.</li>
</ul>
</FactList>

## Timeline

<Timeline items={[
  { date: "2017", title: "Updated front fascia, new", description: "Updated front fascia, new wheel designs, and improved safety features." }
]} />

## Related Terms

<RelatedTerms>
<ul>
<li><strong>Acura</strong> – Luxury vehicle division of Honda.</li>
<li><strong>Crossover SUV</strong> – A type of vehicle combining SUV styling with car-like handling.</li>
<li><strong>Super Handling All</strong> – Wheel Drive (SH-AWD) – Acura’s advanced AWD system for improved traction and performance.</li>
<li><strong>AcuraWatch</strong> – Acura’s suite of advanced driver-assistance systems.</li>
<li><strong>Honda Global Light Truck platform</strong> – Underlying architecture shared with the MDX, Pilot, and RDX.</li>
<li><strong>Luxury vehicle</strong> – A high-end automobile with premium features and materials.</li>
</ul>
</RelatedTerms>

## FAQ

<FAQ>
<details>
<summary>Is the Acura MDX a reliable SUV?</summary>
<p>The MDX has a strong reputation for reliability, consistently earning high marks in dependability studies such as those conducted by [J.D. Power](# "J.D. Power") and [Consumer Reports](# "Consumer Reports"). Regular maintenance and timely software updates help ensure long-term performance.</p>
</details>
<details>
<summary>How does the MDX Type S differ from the standard MDX?</summary>
<p>The MDX Type S features a turbocharged 3.0L V6 engine (vs. the base 3.5L V6), upgraded suspension, sport-tuned exhaust, and unique exterior styling. It delivers higher horsepower (over 350 hp) and quicker acceleration, along with exclusive interior trim and advanced performance features.</p>
</details>
<details>
<summary>What is the fuel economy of the MDX Hybrid?</summary>
<p>The MDX Hybrid is EPA-rated at approximately 25 mpg in city driving and 28 mpg on the highway, a notable improvement over the non-hybrid models which typically achieve around 20 mpg combined.</p>
</details>
<details>
<summary>Does the MDX come with third-row seating?</summary>
<p>Yes, all MDX models feature a three-row seating configuration, accommodating up to seven or eight passengers depending on the trim and configuration.</p>
</details>
<details>
<summary>What safety features are included in the MDX?</summary>
<p>The MDX includes AcuraWatch, which typically features collision mitigation braking, adaptive cruise control, lane-keeping assist, road departure mitigation, and traffic sign recognition. Higher trims may include additional features like blind-spot monitoring and rear cross-traffic alert.</p>
</details>
<details>
<summary>Is the MDX available with all-wheel drive?</summary>
<p>Yes, all MDX models are available with Acura’s Super Handling All-Wheel Drive (SH-AWD), which provides enhanced traction and handling in various driving conditions.</p>
</details>
</FAQ>

## References

<ReferenceList>
<ol>
<li>Acura. (2024). *2025 Acura MDX Product Information*. Retrieved from https://www.acura.com</li>
<li>Honda Motor Co. (2023). *Global Light Truck Platform Overview*. Internal Technical Documentation.</li>
<li>J.D. Power. (2023). *Vehicle Dependability Study*. Retrieved from https://www.jdpower.com</li>
<li>Consumer Reports. (2024). *2024 Acura MDX Review*. Retrieved from https://www.consumerreports.org</li>
<li>EPA. (2023). *Fuel Economy Data for 2023 Acura MDX Hybrid*. Retrieved from https://www.fueleconomy.gov</li>
</ol>
</ReferenceList>

